import {DeleteFilled, LoadingOutlined, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Checkbox, Descriptions, Form, Input, message, Modal, Select, Spin, Table, Tag} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AppContextContext, SbomProjectGroupServiceContext, SbomProjectServiceContext, SbomProjectVersionServiceContext, SoftwareLicensePolicyServiceContext} from "../../Contexts";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {DocumentTitle} from "../DocumentTitle";
import * as globalStyles from "../App.module.css";
import SbomProject from "../../domain/SbomProject";
import SbomProjectVersion from "../../domain/SbomProjectVersion";
import PagedResult from "../../service/PagedResult";
import * as styles from "./SbomProjectDetail.module.css";
import debounce from "lodash/debounce";
import {ColumnsType} from "antd/es/table";
import {formatDateTime} from "../../utils/FormatUtils";
import FindingAnalysisStatistics, {AnalysisStyle} from "./FindingAnalysisStatistics";
import {useTableHandler} from "../../sal-ui/TableHandler";
import {descendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import {Permission} from "../../domain/auth/Permission";
import SingleResult from "../../service/SingleResult";
import SoftwareLicensePolicy from "../../domain/SoftwareLicensePolicy";
import QueryOptions from "../../sal-ui/QueryOptions";
import SbomProjectGroup from "../../domain/SbomProjectGroup";
import SbomProjectVersionFilter, {createDefaultSbomProjectVersionFilterValues} from "./SbomProjectVersionFilter";
import {FormPersister} from "../../sal-ui/FormPersister";
import {FindingSeverity, getEqualOrGreaterSeverities} from "../../domain/FindingSeverity";
import {FocusedFindingAnalysisHost} from "./FocusedFindingAnalysisHost";
import {SbomProjectVersionFindingStatisticsAction} from "./SbomProjectVersionStatistics";
import VulnerabilitiesLabel from "./VulnerabilitiesLabel";

const serverViolationsHolder = new ServerConstraintViolationsHolder();
const FILTER_DEBOUNCE_MS = 500;

function SbomProjectDetail() {
    const appContext = useContext(AppContextContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const sbomProjectGroupService = useContext(SbomProjectGroupServiceContext);
    const sbomProjectVersionService = useContext(SbomProjectVersionServiceContext);
    const softwareLicensePolicyService = useContext(SoftwareLicensePolicyServiceContext);
    const navigate = useNavigate();
    const {sbomProjectId}: any = useParams();
    const filterPersistentIdent = useMemo(() => `SbomProjectDetail.${sbomProjectId}.versions`, [sbomProjectId]);
    const filterFormPersister = useMemo(() => new FormPersister(filterPersistentIdent), [filterPersistentIdent]);
    const [editMode, setEditMode] = useState(false);
    const tableHandler = useTableHandler("name desc", {reloadFunction: reloadProjectVersions});
    const [sbomProject, setSbomProject] = useState<SingleResult<SbomProject>>();
    const [sbomProjectVersions, setSbomProjectVersions] = useState<PagedResult<SbomProjectVersion>>();
    const [softwareLicensePolicies, setSoftwareLicensePolicies] = useState<PagedResult<SoftwareLicensePolicy>>();
    const [groups, setGroups] = useState<SbomProjectGroup[]>();
    const [filterValues, setFilterValues] = useState<any>();
    const [editForm] = useForm();

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    const columns: ColumnsType<SbomProjectVersion> = [
        {
            dataIndex: "name",
            title: "Name",
            defaultSortOrder: "descend",
            className: styles['column-name'],
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: renderName
        },
        {
            dataIndex: "sbomGeneratedAt",
            title: "SBOM generated",
            width: 160,
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => (value) ? formatDateTime(value) : <i>never</i>
        },
        {
            dataIndex: "sbomImportedAt",
            title: "SBOM imported",
            width: 160,
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => (value) ? formatDateTime(value) : <i>never</i>
        },
        {
            dataIndex: "componentEvaluatedAt",
            title: "Last evaluation",
            width: 160,
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => (value) ? formatDateTime(value) : <i>never</i>
        },
        {
            dataIndex: "analysisStatistics",
            title: "Findings analysis",
            align: "center",
            render: renderAnalysisStatistics
        },
        {
            title: <VulnerabilitiesLabel/>,
            render: renderStatistics
        }
    ];

    const hasManageOrg = appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG);
    const hasManage = hasManageOrg || sbomProject?.permissions?.includes(Permission.SBOM_PROJECT__MANAGE);
    const showProjectDetails = !appContext.user.publicAccessAccount;

    if (hasManage) {
        columns.push(
            {
                dataIndex: "suppressInReports",
                title: "Suppress in reports",
                align: "center",
                render: value => <Checkbox checked={value} disabled={true}/>
            },
            {
                title: "Actions",
                render: renderAction
            }
        );
    }

    const debouncedOnFinishFilter = useMemo(() => debounce(onFinishFilter, FILTER_DEBOUNCE_MS), [filterPersistentIdent]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setEditMode(false);
        setSbomProject(undefined);
        setSbomProjectVersions(undefined);

        reload();

        restoreFilterValues();
    }, [sbomProjectId]);

    useEffect(() => () => debouncedOnFinishFilter.cancel(), [debouncedOnFinishFilter]);

    useEffect(() => {
        if (sbomProject === undefined) {
            return;
        }

        if (hasManage) {
            softwareLicensePolicyService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(setSoftwareLicensePolicies);
        }

        if (hasManageOrg) {
            sbomProjectGroupService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setGroups(value.data));
        }
    }, [sbomProject]);

    return (
        <DocumentTitle title={sbomProject?.data?.name}>
            <FocusedFindingAnalysisHost>
                <Breadcrumb
                    className={globalStyles["common__breadcrumb"]}
                    items={[
                        {title: appContext.config?.appName},
                        {title: <Link to={"/sbom-projects"}>Projects</Link>},
                        {title: sbomProject?.data?.name}
                    ]}
                />

                <Spin spinning={!sbomProject} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{sbomProject?.data?.name}</h1>

                    {hasManage && <>
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                            {hasManageOrg &&
                                <Button danger={true}
                                        title={"Delete"}
                                        onClick={() => {
                                            Modal.confirm({
                                                content: "Do you really want to delete this project?",
                                                okText: "Delete",
                                                cancelText: "Cancel",
                                                okButtonProps: {danger: true},
                                                onOk: () => onDeleteConfirm()
                                            });
                                        }}>Delete</Button>
                            }
                        </div>

                        {renderEditForm()}
                    </>}

                    {showProjectDetails &&
                        <Descriptions column={1} bordered={true} size="small" className={styles.details}>
                            <Descriptions.Item label={"License policy"}>
                                {sbomProject?.data?.softwareLicensePolicy
                                    ? <Link to={`/software-license-policies/${sbomProject.data.softwareLicensePolicy.id}`}>{sbomProject.data.softwareLicensePolicy.name}</Link>
                                    : <i>no policy applied</i>
                                }
                            </Descriptions.Item>

                            {hasManageOrg &&
                                <Descriptions.Item label={"Groups"}>
                                    {sbomProject?.data?.groups?.length
                                        ? sbomProject.data.groups.map(group => <div key={group.id}><Link to={`/sbom-project-groups/${group.id}`}>{group.name}</Link></div>)
                                        : <i>no groups</i>
                                    }
                                </Descriptions.Item>
                            }
                        </Descriptions>
                    }

                    <h2 className={styles.versionsTitle}>Project versions</h2>

                    <SbomProjectVersionFilter
                        className={styles.filter}
                        values={filterValues}
                        persistentStateIdent={filterPersistentIdent}
                        onChange={values => {
                            setFilterValues(values);

                            debouncedOnFinishFilter(values);
                        }}
                    />

                    <div className={globalStyles["common__top-button-bar"]}>
                        <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                        {hasManage &&
                            <Button type={"primary"}
                                    icon={<PlusOutlined/>}
                                    onClick={() => navigate(`/sbom-projects/${sbomProjectId}/add`)}>Add</Button>
                        }
                    </div>

                    <Table className={styles.table}
                           showSorterTooltip={false}
                           loading={tableHandler.loading}
                           dataSource={sbomProjectVersions?.data}
                           size="middle"
                           onChange={tableHandler.onTableChange}
                           pagination={tableHandler.pagination}
                           rowKey="id"
                           onRow={onRow}
                           columns={columns}
                    />
                </Spin>
            </FocusedFindingAnalysisHost>
        </DocumentTitle>
    );

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of project {sbomProject?.data?.name}</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."},
                            {
                                validator: serverViolationsHolder.createServerValidator(
                                    'UNIQUE',
                                    'name, organization_id',
                                    undefined,
                                    {compareLowerCaseValues: true}
                                ),
                                message: "Name is already used."
                            }
                        ]}>
                        <Input maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"softwareLicensePolicy"}
                        label={"Software license policy"}>
                        <Select placeholder={"no policy applied"} allowClear={true}>
                            {softwareLicensePolicies?.data?.map(policy =>
                                <Select.Option key={policy.id} value={policy.id}>{policy.name}</Select.Option>
                            )}
                        </Select>
                    </Form.Item>

                    {hasManageOrg &&
                        <Form.Item
                            name={"groupIds"}
                            label={"Groups"}>
                            <Select mode={"multiple"} placeholder={"no groups"}>
                                {groups?.map(group =>
                                    <Select.Option key={group.id} value={group.id}>{group.name}</Select.Option>
                                )}
                            </Select>
                        </Form.Item>
                    }

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        );
    }

    function renderName(value: any, sbomProjectVersion: SbomProjectVersion) {
        return <>
            <div>
                <Link to={`/sbom-projects/${sbomProjectId}/${sbomProjectVersion.id}`}>{value}</Link>

                {sbomProjectVersion.tags.length > 0 && <span style={{marginLeft: 8}}>{sbomProjectVersion.tags.map(value => <Tag key={value}>{value}</Tag>)}</span>}

                {sbomProjectVersion?.licenseViolations?.length > 0 &&
                    <Tag
                        style={{marginLeft: 8}}
                        color={"orange"}
                        title={"One or more licenses violates project's software license policy."}>
                        license violation
                    </Tag>
                }
            </div>

            {sbomProjectVersion.projectType &&
                <div className={styles.projectType}>{sbomProjectVersion.projectType}</div>
            }
        </>;
    }

    function renderAnalysisStatistics(_: any, sbomProjectVersion: SbomProjectVersion) {
        return <FindingAnalysisStatistics analysisStatistics={sbomProjectVersion?.analysisStatistics} style={AnalysisStyle.PROGRESS}/>;
    }

    function renderStatistics(_: any, sbomProjectVersion: SbomProjectVersion) {
        return <SbomProjectVersionFindingStatisticsAction
            projectVersion={sbomProjectVersion}
            sbomProject={sbomProject}
            className={styles.findingStatisticsAction}
            onAfterFindingStatusSave={reloadProjectVersions}/>;
    }

    function renderAction(_: any, sbomProjectVersion: SbomProjectVersion) {
        if (!hasManage) {
            return null;
        }

        return (
            <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            title: <>Deletion confirmation</>,
                            content: <>Do you really want to delete project version <b>{sbomProjectVersion.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteVersionConfirm(sbomProjectVersion)
                        });
                    }}/>
        );
    }

    function onRow(sbomProjectVersion: SbomProjectVersion) {
        return {onDoubleClick: () => navigate(`/sbom-projects/${sbomProjectId}/${sbomProjectVersion.id}`)};
    }

    function onDeleteVersionConfirm(sbomProjectVersion: SbomProjectVersion) {
        sbomProjectVersionService.delete(sbomProjectVersion)
            .then(() => {
                message.success(<>Project version <b>{sbomProjectVersion.name}</b> successfully deleted.</>);

                reloadProjectVersions();
            });
    }

    function onFinishEdit(values: any) {
        sbomProjectService.update(sbomProjectId!, values)
            .then(
                () => {
                    message.success(<>Project <b>{values.name}</b> successfully updated.</>);

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm() {
        sbomProjectService.delete(sbomProject.data)
            .then(() => {
                message.success(<>Project <b>{sbomProject.data.name}</b> successfully deleted.</>);

                navigate(`/sbom-projects`);
            });
    }

    function reload() {
        sbomProjectService.get(sbomProjectId!).then(result => {
            setSbomProject(result);

            editForm.setFieldsValue({
                ...result.data,
                softwareLicensePolicy: result.data.softwareLicensePolicy?.id,
                groupIds: result.data.groupIds
            });
        });
    }

    function reloadProjectVersions() {
        return sbomProjectVersionService.getList(sbomProjectId, tableHandler.queryOptions).then(versions => {
            tableHandler.updateTotal(versions.total);
            setSbomProjectVersions(versions);
        });
    }

    function onFinishFilter(values: any) {
        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(createRqbQuery(values));
    }

    function restoreFilterValues() {
        const values = filterFormPersister.hasState()
            ? filterFormPersister.loadValues()
            : createDefaultSbomProjectVersionFilterValues();

        setFilterValues(values);

        tableHandler.queryOptions.rqbQuery = createRqbQuery(values);
        tableHandler.queryOptions.skip = 0;
        tableHandler.reload();
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        };

        values.name && rqbQuery.rules.push({
            field: "name",
            operator: "icontains",
            value: values.name
        });

        values.severity && values.severity.length > 0 && rqbQuery.rules.push({
            field: "severity",
            operator: "in",
            value: getEqualOrGreaterSeverities(values.severity as FindingSeverity)
        });

        values.analysisState && values.analysisState.length > 0 && rqbQuery.rules.push({
            field: "analysisState",
            operator: "in",
            value: values.analysisState
        });

        return rqbQuery;
    }

}

export default SbomProjectDetail;
